import type { EnterpriseSite } from '~/services/AccountServer/types'
import type { LegacyMemberResult } from '~/services/LegacyEnterpriseServer/types'

export type LegacyMemberTerminalStatus = 'idle' | 'pending' | 'success' | 'failed' | 'unknown'

export interface LegacyMemberPendingIdentity {
  userId?: number | string | null
  teamId?: number | string | null
  migrationNo?: string | null
  site: EnterpriseSite
}

export interface LegacyMemberPendingRecord extends LegacyMemberPendingIdentity {
  requestId: string
  targetSite: EnterpriseSite
  status: 'RESERVED' | 'UNKNOWN'
  createdAt: number
}

const STORAGE_PREFIX = 'legacy-enterprise-member:add:'

function clean(value: unknown): string {
  return String(value || '').trim()
}

export function legacyMemberPendingIdentityKey(identity: LegacyMemberPendingIdentity): string {
  const userId = clean(identity.userId)
  const teamId = clean(identity.teamId)
  const migrationNo = clean(identity.migrationNo)
  const site = clean(identity.site)
  return userId && teamId && migrationNo && site ? `${userId}:${teamId}:${migrationNo}:${site}` : ''
}

export function legacyMemberPendingStorageKey(identity: LegacyMemberPendingIdentity): string {
  const key = legacyMemberPendingIdentityKey(identity)
  return key ? `${STORAGE_PREFIX}${key}` : ''
}

export function createLegacyMemberRequestId(now = Date.now()): string {
  return `LMADD_${now.toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export function legacyMemberStatusState(result?: LegacyMemberResult | null): LegacyMemberTerminalStatus {
  if (!result?.status) return 'unknown'
  if (result.status === 'APPLIED') return 'success'
  if (result.status === 'REJECTED') return 'failed'
  if (result.status === 'RESERVED' || result.status === 'UNKNOWN') return 'pending'
  return 'unknown'
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage || null
  } catch {
    return null
  }
}

export function legacyMemberPendingStorageAvailable(): boolean {
  const store = storage()
  if (!store) return false
  const key = `${STORAGE_PREFIX}probe`
  try {
    store.setItem(key, '1')
    store.removeItem(key)
    return true
  } catch {
    return false
  }
}

function validRecord(record: LegacyMemberPendingRecord, identity: LegacyMemberPendingIdentity) {
  return Boolean(record && legacyMemberPendingIdentityKey(record) === legacyMemberPendingIdentityKey(identity)
    && (record.targetSite === 'AI' || record.targetSite === 'VIP')
    && /^[a-zA-Z0-9_-]{8,64}$/.test(record.requestId || '')
    && (record.status === 'RESERVED' || record.status === 'UNKNOWN')
    && Number.isFinite(record.createdAt))
}

function parseRecords(value: string | null, identity: LegacyMemberPendingIdentity): LegacyMemberPendingRecord[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value) as LegacyMemberPendingRecord | LegacyMemberPendingRecord[]
    const records = Array.isArray(parsed) ? parsed : [parsed]
    if (!records.length || records.some(record => !validRecord(record, identity))) return []
    return records.filter((record, index) => records.findIndex(item => item.requestId === record.requestId) === index)
  } catch {
    return []
  }
}

export function readLegacyMemberPendings(identity: LegacyMemberPendingIdentity): LegacyMemberPendingRecord[] {
  const key = legacyMemberPendingStorageKey(identity)
  const store = storage()
  if (!key || !store) return []
  try {
    const raw = store.getItem(key)
    const records = parseRecords(raw, identity)
    if (raw && !records.length) store.removeItem(key)
    return records
  } catch {
    return []
  }
}

export function readLegacyMemberPending(identity: LegacyMemberPendingIdentity): LegacyMemberPendingRecord | null {
  const records = readLegacyMemberPendings(identity)
  return records.length ? records[records.length - 1] : null
}

export function rememberLegacyMemberPending(record: LegacyMemberPendingRecord): boolean {
  const key = legacyMemberPendingStorageKey(record)
  const store = storage()
  if (!key || !store) return false
  try {
    const records = readLegacyMemberPendings(record).filter(item => item.requestId !== record.requestId)
    records.push(record)
    store.setItem(key, JSON.stringify(records))
    return true
  } catch {
    return false
  }
}

export function clearLegacyMemberPending(identity: LegacyMemberPendingIdentity, requestId?: string): void {
  const key = legacyMemberPendingStorageKey(identity)
  const store = storage()
  try {
    if (!key || !store) return
    if (!requestId) {
      store.removeItem(key)
      return
    }
    const records = readLegacyMemberPendings(identity).filter(record => record.requestId !== requestId)
    if (records.length) store.setItem(key, JSON.stringify(records))
    else store.removeItem(key)
  } catch {}
}
